import { createElement } from "react";
import { hasMath, toSafeHtml } from "../lib/questionMarkup";
import { renderMathWith, useKatex } from "../lib/mathRenderer";

interface QuestionTextProps {
  /** Raw text straight from the question bank. */
  children: string | null | undefined;
  /** The element to render into. A stem is a heading, an option is a span. */
  as?: "p" | "span" | "div" | "h1" | "h2";
  className?: string | undefined;
}

/**
 * Question text, rendered rather than printed.
 *
 * Everything the bank holds went through React's text path before this, so a
 * student saw `re<ins>g</ins>ulate` with the tags visible and the underline
 * gone, and a maths paper as `\(\frac{x-5}{x(x-1)}\)`. The first makes the
 * question unanswerable; the second makes it unreadable.
 *
 * `dangerouslySetInnerHTML` is load-bearing and safe here for one reason: the
 * HTML is not the bank's. Every character is escaped first and only a nine-tag
 * allowlist is put back, attributes and all else discarded, and the maths is
 * KaTeX's own output. Nothing from the row survives as markup.
 */
export function QuestionText({ children, as = "span", className }: QuestionTextProps) {
  const needsMath = hasMath(children);
  const katex = useKatex(needsMath);

  const html = toSafeHtml(children, renderMathWith(katex));

  return createElement(as, {
    className,
    dangerouslySetInnerHTML: { __html: html },
  });
}
